import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi } from '../lib/api.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { usePersonName } from '../lib/people.js';
import { exact, stamp } from '../lib/format.js';
import { SupportError } from '../lib/support-api.js';
import {
  CATEGORY_LABELS,
  ESCALATION_LABELS,
  ESCALATION_STATUSES,
  HOLD_STATUSES,
  STATUS_LABELS,
  TARGET_LABELS,
  type EscalationStatus,
  type EscalationReason,
  type ReportDetail,
  type ReportEvent,
  type ReportNote,
} from '../lib/reports-api.js';
import { Screen } from '../components/Screen.js';
import { Select } from '../components/Select.js';
import { Avatar, Badge, Button, Field, HoldButton, Note, OutcomeNote, Panel, When, inputStyle } from '../components/ui.js';
import { api, PersonName } from './reports-shared.js';
import { EvidencePanel } from './ReportEvidence.js';

/**
 * One report, as a case file (Jon chose the layout, 2026-10-24): the title and
 * where it stands, the two people side by side, what happened, the reported
 * material SEALED until somebody asks to see it, then what to do, then one
 * record of everything done and said.
 *
 * ── NEVER SHOCK UNNECESSARILY ────────────────────────────────────────
 *
 * Nothing reported is on screen when the page opens. A note's text is behind
 * "Show text"; a photo is never drawn here at all, only opened in a new tab
 * through a five-minute link the product signs, and every opening is recorded
 * on the report. If it is abuse imagery, this page is not where it should
 * appear unasked, or be cached.
 *
 * ── A BAN IS AUTH'S, THEN RECORDED HERE ──────────────────────────────
 *
 * The ban itself goes to auth's admin ban-user with this person's own session
 * (as on the user screen), so auth decides whether they may ban. Only then is
 * it recorded on the report. If the record fails the ban is still in force,
 * and the page says so and offers to record it again rather than ban twice.
 */
export function ReportCaseFile({ id, navigate, onChanged }: { id: string; navigate: (to: string) => void; onChanged: () => void }) {
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  // Opening the page is what "viewed" means; it answers with the report.
  useEffect(() => {
    let live = true;
    api
      .viewed(id)
      .then((r) => {
        if (!live) return;
        setReport(r);
        onChanged();
      })
      .catch((e: Error) => live && setFailed(e instanceof SupportError && e.status === 404 ? `There is no report ${id}.` : e.message));
    return () => {
      live = false;
    };
  }, [id]);

  const update = (r: ReportDetail) => {
    setReport(r);
    onChanged();
  };

  if (failed) return <Screen><Note tone="danger">{failed}</Note></Screen>;
  if (!report) return <Screen><Note>Loading…</Note></Screen>;

  return (
    <Screen>
      <div style={{ display: 'grid', gap: '1rem', maxWidth: 1040 }}>
        <TitleBar report={report} navigate={navigate} />
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <PartyPanel
            title="Reporter"
            userId={report.reporterUserId}
            facts={[
              `${report.history.reporterFiled} report${report.history.reporterFiled === 1 ? '' : 's'} filed`,
              report.alsoBlocked ? 'Blocked them when reporting' : null,
            ]}
          />
          <PartyPanel
            title="Reported person"
            userId={report.reportedUserId}
            tone={report.permanentlyBannedAt ? 'danger' : undefined}
            facts={[
              `${report.history.reportedAgainst} report${report.history.reportedAgainst === 1 ? '' : 's'} about them, ${report.history.reportedAgainstActive} active`,
              report.permanentlyBannedAt ? 'Banned permanently' : report.temporaryBanUntil ? `Banned until ${stamp(report.temporaryBanUntil)}` : null,
            ]}
          />
        </div>
        <WhatHappened report={report} />
        {report.legalHold ? <HoldBanner report={report} /> : null}
        <EvidencePanel report={report} onChange={update} />
        <Actions report={report} onChange={update} />
        <Record report={report} onChange={update} />
      </div>
    </Screen>
  );
}

const num: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

const STATE_TEXT = { NEW: 'New', ESCALATED: 'Escalated', ARCHIVED: 'Archived' } as const;

function TitleBar({ report: r, navigate }: { report: ReportDetail; navigate: (to: string) => void }) {
  const back = r.state === 'ARCHIVED' ? 'archived' : r.state === 'ESCALATED' ? 'escalated' : 'new';
  const child = r.category === 'CHILD_SAFETY';
  return (
    <header style={{ display: 'grid', gap: '.45rem' }}>
      <a
        href={`#/reports/${back}`}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/reports/${back}`);
        }}
        style={{ fontSize: '.85rem', fontWeight: 500, color: C.textMid2, textDecoration: 'none', justifySelf: 'start' }}
      >
        ← {STATE_TEXT[r.state]}
      </a>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap' }}>
        <h1 style={{ ...num, margin: 0, fontSize: '1.55rem', fontWeight: 760, letterSpacing: '-.01em' }}>{r.id}</h1>
        <Stamp report={r} />
        <Badge tone={child ? 'danger' : 'neutral'}>{CATEGORY_LABELS[r.category]}</Badge>
        <span style={{ color: C.textMid2, fontSize: '.88rem' }}>
          {TARGET_LABELS[r.targetKind]} in {r.product} · reported <When iso={r.reportedAt} />
        </span>
      </div>
    </header>
  );
}

/** Where the report stands, as a stamp: the one loud thing in the title bar. */
function Stamp({ report: r }: { report: ReportDetail }) {
  const authorities = r.escalationReason === 'REPORT_TO_AUTHORITIES';
  const look =
    r.state === 'ESCALATED'
      ? { fg: C.dangerText, br: C.danger, text: `Escalated: ${ESCALATION_LABELS[r.escalationReason ?? 'PERMANENT_BAN_REVIEW'].toLowerCase()}` }
      : r.state === 'ARCHIVED'
        ? { fg: C.textMid, br: C.inputBorder, text: 'Archived' }
        : { fg: C.blueText, br: C.brand, text: 'New' };
  return (
    <span
      style={{
        padding: '.2rem .6rem',
        border: `1.5px solid ${look.br}`,
        borderRadius: 6,
        color: look.fg,
        fontSize: '.74rem',
        fontWeight: 760,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        background: r.state === 'ESCALATED' && authorities ? C.dangerBg : 'transparent',
      }}
    >
      {look.text}
    </span>
  );
}

function PartyPanel({
  title,
  userId,
  facts,
  tone,
}: {
  title: string;
  userId: string | null;
  facts: (string | null)[];
  tone?: 'danger';
}) {
  const { state } = useAsync(() => (userId ? adminApi.user(userId) : Promise.resolve(null)), [userId]);
  const user = state.status === 'ready' ? state.data : null;
  const name = user?.name || user?.email || userId;
  return (
    <Panel title={title}>
      <div style={{ padding: '.9rem 1.2rem 1rem', display: 'grid', gap: '.6rem' }}>
        {userId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', minWidth: 0 }}>
            <Avatar id={userId} label={name ?? '?'} image={user?.image ?? null} size={40} />
            <div style={{ minWidth: 0 }}>
              <a href={`#/users/${encodeURIComponent(userId)}`} style={{ display: 'block', fontWeight: 650, color: C.text, overflowWrap: 'anywhere' }}>
                {state.status === 'loading' ? 'Loading…' : name}
              </a>
              <span style={{ fontSize: '.84rem', color: C.textMid2, overflowWrap: 'anywhere' }}>
                {state.status === 'failed' ? 'Account not found' : (user?.email ?? 'No email on file')}
              </span>
            </div>
          </div>
        ) : (
          <span style={{ color: C.textMid2 }}>This report does not name a person.</span>
        )}
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 3, fontSize: '.86rem', color: tone ? C.dangerText : C.textMid }}>
          {facts
            .filter((f): f is string => f !== null)
            .map((f) => (
              <li key={f}>{f}</li>
            ))}
        </ul>
      </div>
    </Panel>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    // Label beside value on a desktop; the value wraps under its label on a phone.
    <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '1rem', rowGap: '.15rem', padding: '.55rem 0', borderBottom: `1px solid ${C.border2}` }}>
      <dt style={{ flex: '0 0 150px', color: C.textMid2, fontSize: '.86rem' }}>{label}</dt>
      <dd style={{ flex: '1 1 260px', margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</dd>
    </div>
  );
}

function WhatHappened({ report: r }: { report: ReportDetail }) {
  const o = r.reporterOrigin;
  const place = [o.city, o.region, o.country].filter(Boolean).join(', ');
  return (
    <Panel title="What happened">
      <dl style={{ margin: 0, padding: '.3rem 1.2rem .7rem' }}>
        <Fact label="Reason">
          {CATEGORY_LABELS[r.category]}
          {/* The app's own reason only when this service did not know it, so the words staff read are never a slug. */}
          {r.category === 'OTHER' && r.reasonId !== 'other' ? (
            <span style={{ color: C.textMid2 }}> · the app said “{r.reasonId}”</span>
          ) : null}
        </Fact>
        <Fact label="Reported">
          <time dateTime={r.reportedAt} title={exact(r.reportedAt)}>{stamp(r.reportedAt)}</time>
        </Fact>
        <Fact label="Happened">
          {r.reportedEventAt ? (
            <time dateTime={r.reportedEventAt} title={exact(r.reportedEventAt)}>{stamp(r.reportedEventAt)}</time>
          ) : (
            <span style={{ color: C.textMid2 }}>The app did not say</span>
          )}
        </Fact>
        <Fact label="Reported from">
          {o.ip ? <span style={num}>{o.ip}</span> : <span style={{ color: C.textMid2 }}>No address recorded</span>}
          {place ? <span style={{ color: C.textMid }}> · {place}</span> : o.ip ? <span style={{ color: C.textMid2 }}> · no location on the request</span> : null}
        </Fact>
        <Fact label="Their words">
          {r.details ? (
            <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{r.details}</p>
          ) : (
            <span style={{ color: C.textMid2 }}>They added nothing.</span>
          )}
        </Fact>
      </dl>
    </Panel>
  );
}

// ── What to do ──────────────────────────────────────────────────────────

function Actions({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const archivedBy = usePersonName(r.archivedByUserId);
  if (r.state === 'ARCHIVED') {
    return (
      <Panel title="What to do">
        <div style={{ padding: '1rem 1.2rem', display: 'grid', gap: '.8rem' }}>
          <p style={{ margin: 0, color: C.textMid }}>
            {r.escalationStatus === 'CLOSED' ? 'Closed' : 'Archived'} by {archivedBy ?? 'staff'} <When iso={r.archivedAt} />. It is kept for seven years.
          </p>
          {r.contentHiddenAt ? <RemoveContent report={r} onChange={onChange} /> : null}
        </div>
      </Panel>
    );
  }
  return (
    <Panel title="What to do">
      <div style={{ display: 'grid' }}>
        {r.state === 'ESCALATED' ? <EscalatedBanner report={r} /> : null}
        {r.state === 'ESCALATED' ? <StatusControl report={r} onChange={onChange} /> : null}
        {r.state === 'ESCALATED' && r.reportedUserId ? <PermanentBan report={r} onChange={onChange} /> : null}
        {r.targetKind !== 'PERSON' ? (
          <Step
            title="Remove content"
            hint={
              r.legalHold
                ? 'Under a legal hold the content stays removed.'
                : 'The app shows “Removed by admin.” in its place. The evidence stays here either way, and the content can be restored.'
            }
          >
            <RemoveContent report={r} onChange={onChange} />
          </Step>
        ) : null}
        {r.state === 'NEW' && r.reportedUserId ? <TemporaryBan report={r} onChange={onChange} /> : null}
        {r.state === 'NEW' ? <Escalate report={r} onChange={onChange} /> : null}
        {r.state === 'NEW' ? <Archive report={r} onChange={onChange} /> : null}
      </div>
    </Panel>
  );
}

function Step({ title, hint, tone, children }: { title: string; hint?: ReactNode; tone?: 'danger'; children: ReactNode }) {
  return (
    <section style={{ padding: '1rem 1.2rem', borderBottom: `1px solid ${C.border2}`, display: 'grid', gap: '.6rem' }}>
      <div>
        <h3 style={{ margin: 0, fontSize: '.92rem', fontWeight: 680, color: tone ? C.dangerText : C.text }}>{title}</h3>
        {hint ? <p style={{ margin: '.2rem 0 0', fontSize: '.84rem', color: C.textMid2, lineHeight: 1.45 }}>{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function EscalatedBanner({ report: r }: { report: ReportDetail }) {
  const by = usePersonName(r.escalatedByUserId);
  const note = [...r.events].reverse().find((e) => e.kind === 'ESCALATED')?.detail?.note;
  const authorities = r.escalationReason === 'REPORT_TO_AUTHORITIES';
  return (
    <div style={{ padding: '1rem 1.2rem 0' }}>
      <div
        style={{
          padding: '.8rem 1rem',
          borderRadius: 8,
          border: `1px solid ${authorities ? C.dangerBorder : C.warnBg}`,
          background: authorities ? C.dangerBg : C.warnBg,
          color: authorities ? C.dangerText : C.warnText,
          lineHeight: 1.5,
        }}
      >
        <strong>{r.escalationReason ? ESCALATION_LABELS[r.escalationReason] : 'Escalated'}</strong>, escalated by {by ?? 'staff'} <When iso={r.escalatedAt} />.
        {typeof note === 'string' ? <span style={{ display: 'block', marginTop: 4 }}>“{note}”</span> : null}
      </div>
    </div>
  );
}

function RemoveContent({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const action = useAction();
  const hidden = Boolean(r.contentHiddenAt);
  if (hidden && r.legalHold) {
    return (
      <p style={{ margin: 0, color: C.textMid }}>
        Removed <When iso={r.contentHiddenAt} />.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: '.5rem', justifyItems: 'start' }}>
      <OutcomeNote outcome={action.outcome} />
      <Button
        tone={hidden ? undefined : 'danger'}
        disabled={action.running}
        onClick={() =>
          void action.run(async () => {
            onChange(hidden ? await api.restoreContent(r.id) : await api.hideContent(r.id));
            return hidden ? 'Restored. People see it again.' : 'Removed. People see “Removed by admin.” instead.';
          })
        }
      >
        {action.running ? 'Working…' : hidden ? 'Restore content' : 'Remove content'}
      </Button>
    </div>
  );
}

const DAYS = [
  { value: '1', label: '1 day' },
  { value: '3', label: '3 days' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
] as const;

/**
 * Ban in auth, then record. A failed record keeps what was applied, so "Record
 * it" retries the record alone and the person is never banned twice.
 */
function useBan(r: ReportDetail, onChange: (r: ReportDetail) => void) {
  const action = useAction();
  const [unrecorded, setUnrecorded] = useState<{ kind: 'temporary'; reason: string; days: number } | { kind: 'permanent'; reason: string } | null>(null);
  const record = (ban: NonNullable<typeof unrecorded>) =>
    action.run(async () => {
      try {
        onChange(await api.recordBan(r.id, ban));
      } catch (e) {
        setUnrecorded(ban);
        throw new Error(`The ban is in force in auth, but it was not recorded on this report: ${(e as Error).message}`, { cause: e });
      }
      setUnrecorded(null);
      return ban.kind === 'temporary' ? `Banned for ${ban.days} days, and recorded.` : 'Banned permanently, and recorded.';
    });
  const ban = (b: NonNullable<typeof unrecorded>) =>
    action.run(async () => {
      await adminApi.banUser(r.reportedUserId!, b.reason, b.kind === 'temporary' ? b.days * 86_400 : undefined);
      try {
        onChange(await api.recordBan(r.id, b));
      } catch (e) {
        setUnrecorded(b);
        throw new Error(`The ban is in force in auth, but it was not recorded on this report: ${(e as Error).message}`, { cause: e });
      }
      return b.kind === 'temporary' ? `Banned for ${b.days} days. Their sessions end now.` : 'Banned permanently. Their sessions end now.';
    });
  return { action, ban, unrecorded, record };
}

function TemporaryBan({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const [reason, setReason] = useState('');
  const [days, setDays] = useState<(typeof DAYS)[number]['value']>('7');
  const { action, ban, unrecorded, record } = useBan(r, onChange);
  return (
    <Step
      title="Temporary ban"
      hint={r.temporaryBanUntil ? `Already banned until ${stamp(r.temporaryBanUntil)}. A new ban replaces it.` : 'Signs them out everywhere and stops them signing in until it ends.'}
    >
      <OutcomeNote outcome={action.outcome} />
      {/* Top, not end: Reason has a hint under it, so aligning bottoms pushed Length's label and select below Reason's. */}
      <div style={{ display: 'grid', gap: '.6rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', alignItems: 'start' }}>
        <Field label="Reason" hint="Required. Kept on their account and on this report.">
          <input value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} maxLength={500} />
        </Field>
        <Field label="Length">
          <Select value={days} options={DAYS} onChange={setDays} label="Ban length" block />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
        <HoldButton
          title="Hold to ban this account. Their sessions end at once."
          disabled={action.running || reason.trim().length < 3}
          onHold={() => void ban({ kind: 'temporary', reason: reason.trim(), days: Number(days) }).then((ok) => ok && setReason(''))}
        >
          {action.running ? 'Working…' : `Ban for ${DAYS.find((d) => d.value === days)?.label}`}
        </HoldButton>
        {unrecorded ? <Button onClick={() => void record(unrecorded)}>Record it</Button> : null}
      </div>
    </Step>
  );
}

function PermanentBan({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const [reason, setReason] = useState('');
  const { action, ban, unrecorded, record } = useBan(r, onChange);
  if (r.permanentlyBannedAt) {
    return (
      <Step title="Permanent ban" tone="danger">
        <p style={{ margin: 0, color: C.textMid }}>
          Banned permanently <When iso={r.permanentlyBannedAt} />.
        </p>
      </Step>
    );
  }
  return (
    <Step title="Permanent ban" tone="danger" hint="Ends their sessions and stops them signing in, with no end date. Lifting it later is done on their account.">
      <OutcomeNote outcome={action.outcome} />
      <Field label="Reason" hint="Required. Kept on their account and on this report.">
        <input value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} maxLength={500} />
      </Field>
      <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
        <HoldButton
          title="Hold to ban this account permanently."
          disabled={action.running || reason.trim().length < 3}
          onHold={() => void ban({ kind: 'permanent', reason: reason.trim() })}
        >
          {action.running ? 'Working…' : 'Ban permanently'}
        </HoldButton>
        {unrecorded ? <Button onClick={() => void record(unrecorded)}>Record it</Button> : null}
      </div>
    </Step>
  );
}

/**
 * An escalated report's status (Jon, 2026-10-24): five steps, any of which can
 * be chosen. Choosing one shows what it needs: the CyberTipline number for
 * Awaiting authorities (which also needs the content removed), who asked for
 * Secured, and a note to leave a legal hold. The server holds the same rules.
 */
function StatusControl({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const current = r.escalationStatus ?? 'ESCALATED';
  const [target, setTarget] = useState<EscalationStatus | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const action = useAction();
  const leavingHold = target !== null && HOLD_STATUSES.includes(current) && !HOLD_STATUSES.includes(target);
  const needsReference = target === 'AWAITING_AUTHORITIES' || target === 'SECURED';
  const blockedByContent = target === 'AWAITING_AUTHORITIES' && r.targetKind !== 'PERSON' && !r.contentHiddenAt;
  const ready =
    target !== null &&
    !blockedByContent &&
    (!needsReference || reference.trim().length >= 3) &&
    (!leavingHold || note.trim().length >= 5);

  return (
    <Step title="Status" hint="Where this escalation stands. Awaiting authorities and Secured put it under a legal hold.">
      <OutcomeNote outcome={action.outcome} />
      <div role="radiogroup" aria-label="Escalation status" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {ESCALATION_STATUSES.map((s, i) => {
          const isCurrent = s === current;
          const chosen = s === target;
          const hold = HOLD_STATUSES.includes(s);
          return (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={isCurrent || chosen}
              disabled={isCurrent || action.running}
              onClick={() => {
                setTarget(chosen ? null : s);
                setReference('');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '.4rem .7rem',
                borderRadius: 999,
                fontSize: '.84rem',
                fontWeight: isCurrent || chosen ? 680 : 550,
                cursor: isCurrent ? 'default' : 'pointer',
                border: `1px solid ${isCurrent ? C.brand : chosen ? (hold ? C.danger : C.brand) : C.inputBorder}`,
                background: isCurrent ? C.blueTint : chosen ? (hold ? C.dangerBg : C.surface) : 'transparent',
                color: isCurrent ? C.blueText : hold ? C.dangerText : C.text,
              }}
            >
              <span style={{ ...num, fontSize: '.72rem', color: isCurrent ? C.blueText : C.textMid2 }}>{i + 1}</span>
              {STATUS_LABELS[s]}
              {isCurrent ? <span style={{ fontSize: '.72rem', fontWeight: 600 }}>now</span> : null}
            </button>
          );
        })}
      </div>

      {target ? (
        <div style={{ display: 'grid', gap: '.6rem', paddingTop: '.2rem' }}>
          {target === 'AWAITING_AUTHORITIES' ? (
            <p style={{ margin: 0, fontSize: '.86rem', color: C.textMid, lineHeight: 1.45 }}>
              File it with NCMEC’s{' '}
              <a href="https://report.cybertip.org/" target="_blank" rel="noopener noreferrer" style={{ color: C.blueText }}>
                CyberTipline
              </a>
              , then enter the report number. The evidence is then held for at least a year.
            </p>
          ) : null}
          {blockedByContent ? <Note tone="warn">Remove the content first: material reported to the authorities does not stay up.</Note> : null}
          {needsReference ? (
            <Field label={target === 'AWAITING_AUTHORITIES' ? 'CyberTipline report number' : 'Who asked to keep it, and their reference'}>
              <input value={reference} onChange={(e) => setReference(e.target.value)} style={{ ...inputStyle, ...num }} maxLength={300} />
            </Field>
          ) : null}
          <Field label={leavingHold ? 'Why the hold can be lifted (required)' : 'Note (optional)'}>
            <input value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} maxLength={2000} />
          </Field>
          <div style={{ display: 'flex', gap: '.6rem' }}>
            <Button
              tone={HOLD_STATUSES.includes(target) ? 'danger' : undefined}
              disabled={!ready || action.running}
              onClick={() =>
                void action
                  .run(async () => {
                    onChange(
                      await api.setStatus(r.id, target, {
                        ...(reference.trim() ? { reference: reference.trim() } : {}),
                        ...(note.trim() ? { note: note.trim() } : {}),
                      }),
                    );
                    return `Moved to ${STATUS_LABELS[target]}.`;
                  })
                  .then((ok) => {
                    if (ok) {
                      setTarget(null);
                      setReference('');
                      setNote('');
                    }
                  })
              }
            >
              {action.running ? 'Working…' : `Move to ${STATUS_LABELS[target]}`}
            </Button>
            <Button onClick={() => setTarget(null)}>Cancel</Button>
          </div>
        </div>
      ) : null}
    </Step>
  );
}

/** What the legal hold means while it is in force, said once, above the evidence. */
function HoldBanner({ report: r }: { report: ReportDetail }) {
  return (
    <section
      role="note"
      style={{ padding: '.9rem 1.1rem', borderRadius: 10, border: `1px solid ${C.dangerBorder}`, background: C.dangerBg, color: C.dangerText, lineHeight: 1.5 }}
    >
      <strong>Legal hold: {r.escalationStatus ? STATUS_LABELS[r.escalationStatus] : ''}</strong>
      {r.escalationStatus === 'AWAITING_AUTHORITIES' && r.authoritiesReference ? (
        <span> · CyberTipline report <span style={num}>{r.authoritiesReference}</span></span>
      ) : null}
      {r.escalationStatus === 'SECURED' && r.securedReference ? <span> · kept at the request of {r.securedReference}</span> : null}
      <ul style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem', fontSize: '.86rem' }}>
        <li>The content stays removed; it cannot be restored.</li>
        <li>Images open only for a Global Admin, who must say why. Every opening is recorded.</li>
        <li>Nothing is deleted, by retention or by anyone, except at a law enforcement request.</li>
        <li>Leaving the hold needs a note saying why.</li>
      </ul>
    </section>
  );
}

/**
 * Two reasons, and reporting to authorities is the heavier one (Jon,
 * 2026-10-24): listed first, wider, in the alarm color, and saying what it
 * leads to. The lighter one is plainly a second choice.
 */
function Escalate({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const [reason, setReason] = useState<EscalationReason | null>(r.category === 'CHILD_SAFETY' ? 'REPORT_TO_AUTHORITIES' : null);
  const [note, setNote] = useState('');
  const action = useAction();
  const choice = (value: EscalationReason, title: string, body: string, heavy: boolean) => {
    const on = reason === value;
    const color = heavy ? C.danger : C.brand;
    return (
      <label
        style={{
          flex: heavy ? '2 1 280px' : '1 1 220px',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '.6rem',
          alignItems: 'start',
          padding: heavy ? '1rem' : '.8rem 1rem',
          borderRadius: 8,
          cursor: 'pointer',
          border: `${on ? 2 : 1}px solid ${on ? color : heavy ? C.dangerBorder : C.border}`,
          background: heavy ? C.dangerBg : C.surface,
        }}
      >
        <input type="radio" name={`escalate-${r.id}`} checked={on} onChange={() => setReason(value)} style={{ marginTop: 3, accentColor: color }} />
        <span>
          <span style={{ display: 'block', fontWeight: 700, fontSize: heavy ? '.98rem' : '.9rem', color: heavy ? C.dangerText : C.text }}>{title}</span>
          <span style={{ display: 'block', marginTop: 2, fontSize: '.84rem', lineHeight: 1.45, color: heavy ? C.dangerText : C.textMid2 }}>{body}</span>
        </span>
      </label>
    );
  };
  return (
    <Step title="Escalate" hint="For a decision only an admin takes. It moves to Escalated, where the permanent ban and the report to authorities are recorded.">
      <OutcomeNote outcome={action.outcome} />
      <div style={{ display: 'flex', gap: '.7rem', flexWrap: 'wrap' }} role="radiogroup" aria-label="Why escalate">
        {choice(
          'REPORT_TO_AUTHORITIES',
          'Report to authorities',
          'Apparent child sexual abuse material, or somebody in danger. Leads to a CyberTipline report to NCMEC, which the law requires for CSAM.',
          true,
        )}
        {choice('PERMANENT_BAN_REVIEW', 'Permanent ban review', 'Serious or repeated harm that a temporary ban will not fix.', false)}
      </div>
      <Field label="Note for whoever picks it up (optional)">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
      </Field>
      <div>
        <Button
          tone={reason === 'REPORT_TO_AUTHORITIES' ? 'danger' : undefined}
          disabled={action.running || !reason}
          onClick={() =>
            void action.run(async () => {
              onChange(await api.escalate(r.id, reason!, note.trim() || undefined));
              return 'Escalated.';
            })
          }
        >
          {action.running ? 'Working…' : reason ? `Escalate: ${ESCALATION_LABELS[reason].toLowerCase()}` : 'Choose why first'}
        </Button>
      </div>
    </Step>
  );
}

function Archive({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const [note, setNote] = useState('');
  const action = useAction();
  return (
    <Step title="Archive" hint="When nothing more is owed. Archived reports are kept for seven years, then deleted.">
      <OutcomeNote outcome={action.outcome} />
      <div style={{ display: 'flex', gap: '.6rem', alignItems: 'end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px' }}>
          <Field label="What was decided (optional)">
            <input value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} maxLength={2000} />
          </Field>
        </div>
        <Button
          disabled={action.running}
          onClick={() =>
            void action.run(async () => {
              onChange(await api.archive(r.id, note.trim() || undefined));
              return 'Archived.';
            })
          }
        >
          {action.running ? 'Working…' : 'Archive'}
        </Button>
      </div>
    </Step>
  );
}

// ── The record: events and notes, in order ─────────────────────────────

type Entry = { at: string; event: ReportEvent; note?: never } | { at: string; note: ReportNote; event?: never };

function Record({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const entries: Entry[] = [
    ...r.events.map((event) => ({ at: event.at, event })),
    ...r.notes.map((note) => ({ at: note.at, note })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const [body, setBody] = useState('');
  const action = useAction();

  return (
    <Panel title="Record">
      <ol style={{ listStyle: 'none', margin: 0, padding: '.6rem 1.2rem 0', position: 'relative' }}>
        {entries.map((e) => (
          <li key={e.event?.id ?? e.note!.id} style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: '.75rem', paddingBottom: '.9rem' }}>
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                marginTop: 5,
                borderRadius: 5,
                background: e.note ? C.surface : markColor(e.event!.kind),
                border: `2px solid ${e.note ? C.textMid2 : markColor(e.event!.kind)}`,
              }}
            />
            {e.note ? <NoteEntry note={e.note} /> : <EventEntry event={e.event!} report={r} />}
          </li>
        ))}
      </ol>
      <div style={{ padding: '.4rem 1.2rem 1.1rem', display: 'grid', gap: '.5rem', borderTop: `1px solid ${C.border2}` }}>
        <OutcomeNote outcome={action.outcome} />
        <Field label="Add a note">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={10_000} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </Field>
        <div>
          <Button
            disabled={action.running || !body.trim()}
            onClick={() =>
              void action
                .run(async () => {
                  onChange(await api.addNote(r.id, body.trim()));
                  return 'Note added.';
                })
                .then((ok) => ok && setBody(''))
            }
          >
            {action.running ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function markColor(kind: string): string {
  if (['PERMANENT_BAN', 'ALERT_FAILED', 'SNAPSHOT_FAILED', 'EVIDENCE_DESTROYED'].includes(kind)) return C.danger;
  if (['ESCALATED', 'STATUS_CHANGED', 'TEMPORARY_BAN', 'CONTENT_HIDDEN', 'EVIDENCE_OPENED'].includes(kind)) return C.warnDot;
  if (kind === 'ARCHIVED') return C.successDot;
  return C.mutedDot;
}

function Who({ id }: { id: string | null }) {
  return <strong style={{ fontWeight: 650 }}>{id ? <PersonName id={id} /> : 'The service'}</strong>;
}

function EventEntry({ event: e, report: r }: { event: ReportEvent; report: ReportDetail }) {
  const d = e.detail ?? {};
  const text = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : null);
  let what: ReactNode;
  switch (e.kind) {
    case 'FILED':
      what = <><Who id={r.reporterUserId} /> filed the report</>;
      break;
    case 'ALERT_SENT':
      what = <>Child-safety email sent to {text('to')}</>;
      break;
    case 'ALERT_FAILED':
      what = <>Child-safety email to {text('to')} was not sent ({text('code')})</>;
      break;
    case 'VIEWED':
      what = <><Who id={e.actorUserId} /> opened it{d.sinceEscalation ? ' after it was escalated' : ' for the first time'}</>;
      break;
    case 'CONTENT_HIDDEN':
      what = <><Who id={e.actorUserId} /> removed the content</>;
      break;
    case 'CONTENT_RESTORED':
      what = <><Who id={e.actorUserId} /> restored the content</>;
      break;
    case 'SNAPSHOT_CAPTURED':
      what = e.actorUserId ? (
        <><Who id={e.actorUserId} /> captured the evidence again ({String(d.items)} item{d.items === 1 ? '' : 's'})</>
      ) : (
        <>Evidence captured from {r.product} ({String(d.items)} item{d.items === 1 ? '' : 's'})</>
      );
      break;
    case 'SNAPSHOT_FAILED':
      what = <>Evidence could not be captured from {r.product} ({text('code')})</>;
      break;
    case 'EVIDENCE_OPENED':
      what = <><Who id={e.actorUserId} /> opened the {d.role === 'avatar' ? 'profile picture' : 'reported image'}</>;
      break;
    case 'EVIDENCE_DESTROYED':
      what = <><Who id={e.actorUserId} /> destroyed {String(d.items)} image{d.items === 1 ? '' : 's'} at law enforcement request {text('requestReference')}</>;
      break;
    case 'STATUS_CHANGED':
      what = (
        <>
          <Who id={e.actorUserId} /> moved it to {STATUS_LABELS[text('to') as EscalationStatus] ?? text('to')}
          {text('reference') ? <> (<span style={num}>{text('reference')}</span>)</> : null}
          {d.holdLifted ? ', lifting the legal hold' : ''}
        </>
      );
      break;
    case 'MATERIAL_OPENED':
      what = <><Who id={e.actorUserId} /> opened the photo</>;
      break;
    case 'TEMPORARY_BAN':
      what = <><Who id={e.actorUserId} /> banned <PersonName id={text('userId')} /> for {String(d.days)} days: {text('reason')}</>;
      break;
    case 'PERMANENT_BAN':
      what = <><Who id={e.actorUserId} /> banned <PersonName id={text('userId')} /> permanently: {text('reason')}</>;
      break;
    case 'ESCALATED':
      what = <><Who id={e.actorUserId} /> escalated it for {ESCALATION_LABELS[text('reason') as EscalationReason]?.toLowerCase() ?? text('reason')}</>;
      break;
    case 'REPORTED_TO_AUTHORITIES':
      what = <><Who id={e.actorUserId} /> recorded CyberTipline report <span style={num}>{text('reference')}</span></>;
      break;
    case 'ARCHIVED':
      what = <><Who id={e.actorUserId} /> archived it</>;
      break;
    default:
      what = <><Who id={e.actorUserId} /> {e.kind.toLowerCase().replace(/_/g, ' ')}</>;
  }
  const quote = ['ESCALATED', 'ARCHIVED', 'STATUS_CHANGED'].includes(e.kind) ? text('note') : (e.kind === 'EVIDENCE_OPENED' ? text('reason') : null);
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ lineHeight: 1.45, color: C.text, overflowWrap: 'anywhere' }}>
        {what}
        {e.actorIsAgent ? <Badge>agent</Badge> : null}
      </div>
      {quote ? <p style={{ margin: '.25rem 0 0', color: C.textMid, fontSize: '.88rem' }}>“{quote}”</p> : null}
      <time dateTime={e.at} title={exact(e.at)} style={{ fontSize: '.78rem', color: C.textMid2 }}>{stamp(e.at)}</time>
    </div>
  );
}

function NoteEntry({ note: n }: { note: ReportNote }) {
  return (
    <div style={{ minWidth: 0, padding: '.7rem .9rem', background: C.page, border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <div style={{ fontSize: '.84rem', color: C.textMid2, marginBottom: 4 }}>
        <Who id={n.authorUserId} /> wrote · <time dateTime={n.at} title={exact(n.at)}>{stamp(n.at)}</time>
      </div>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5, overflowWrap: 'anywhere' }}>{n.body}</p>
    </div>
  );
}
